/* Il catalogo delle attività: la tassonomia dello studio.
 *
 * **Questa pagina nasce vuota apposta, e non e' una mancanza.** I codici veri
 * li mette chi il mestiere lo fa: dedurli dalle quasi 150 prestazioni del
 * programma vecchio produrrebbe una tassonomia plausibile e sbagliata, che e'
 * il modo piu' sicuro di far fallire l'adozione. E' la stessa famiglia di
 * errore dei numeri plausibili che il motore fiscale insegue, applicata a una
 * struttura invece che a un importo.
 *
 * **Finche' e' vuota non si imputa niente**, perche' l'attivita' e'
 * obbligatoria: e' la prima delle tre leve contro il difetto 2, quella che
 * sostituisce la prosa libera con una scelta da un elenco.
 *
 * Solo l'amministratore arriva qui. Non e' una preferenza di chi imputa: e'
 * la tassonomia dello studio, e chi la cambia cambia come si legge il lavoro
 * di tutti.
 *
 * **Dal 25 settembre 2026 un'attivita' puo' avere un prezzo fisso**, a
 * prestazione o per unita' (i fabbricati di un IMU): il listino vero dello
 * studio e' quasi tutto cosi'. Senza prezzo si fattura a ore, come prima. Il
 * prezzo si cambia riga per riga, e cambia il maturato non ancora fatturato,
 * non quello gia' fatturato, che resta congelato.
 *
 * **Dal 12 ottobre 2026 ogni attivita' ha anche la frase con cui compare nel
 * testo da incollare in Fatture Smart**, quella formale che lo studio usa sui
 * documenti ("Predisposizione ed invio telematico dichiarazione IVA annuale")
 * e non il nome breve della picklist. Vuota: vale il nome. Cambia i testi
 * composti da li' in avanti, mai quelli gia' composti.
 */

import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ErroreApi, anagrafica } from "../api";
import { useCarica } from "../carica";
import { Interruttore } from "../componenti/Interruttore";
import {
  TIPI_PRATICA,
  conta,
  dataCompleta,
  euro,
  importoDaScritto,
  nomeTipo,
  trova,
  type Attivita,
  type UsoAttivita,
} from "../dominio";

export function Catalogo() {
  const catalogo = useCarica<Attivita[]>(() => anagrafica.catalogo(), []);
  const uso = useCarica<UsoAttivita[]>(() => anagrafica.usoCatalogo(), []);
  // Da un'altra schermata (un avviso, un'ora) si arriva qui con il codice gia' nella ricerca.
  const [parametri] = useSearchParams();
  const [cerca, setCerca] = useState(parametri.get("cerca") ?? "");
  const usi = new Map((uso.dati ?? []).map((u) => [u.codice, u]));

  return (
    <>

      <p>
        Sono i codici che chi imputa sceglie da un elenco, invece di scrivere
        prosa libera. Ogni codice vale per uno o più tipi di lavoro:
        quando si imputa su una pratica, compaiono solo quelli che valgono per
        il suo tipo.
      </p>

      {catalogo.errore && (
        <p className="errore" role="alert">
          {catalogo.errore}
        </p>
      )}

      {catalogo.dati === undefined ? (
        <p className="attesa">Un momento.</p>
      ) : (
        <>
          {catalogo.dati.length === 0 ? (
            <p className="errore">
              Il catalogo è vuoto, e finché è così nessuno può imputare ore:
              l'attività è obbligatoria. I codici li decide chi conosce il
              lavoro dello studio, non il programma.
            </p>
          ) : (
            <>
              <div className="campo campo-largo">
                <label htmlFor="cerca-attivita">Cerca un'attività</label>
                <input
                  id="cerca-attivita"
                  type="search"
                  value={cerca}
                  placeholder="codice o descrizione, per esempio imu"
                  onChange={(e) => setCerca(e.target.value)}
                />
              </div>
              <PerTipo
                attivita={catalogo.dati.filter((a) => trova(cerca, a.codice, a.descrizione, a.testo_fattura))}
                tutte={catalogo.dati.length}
                dopo={catalogo.ricarica}
                usi={usi}
              />
            </>
          )}
          <details className="nuova-attivita" open={catalogo.dati.length === 0}>
            <summary>Aggiungi un'attività al catalogo</summary>
            <NuovaAttivita dopo={catalogo.ricarica} />
          </details>
        </>
      )}
    </>
  );
}

/** Raggruppate per tipo di lavoro e non in un elenco piatto, perche' la
 * domanda vera non e' "quali codici esistono" ma "chi lavora su un bilancio
 * cosa puo' scegliere". Un tipo senza nessun codice e' un buco che manda
 * l'operatore a sbattere, e qui si vede a colpo d'occhio. */
function PerTipo({
  attivita,
  tutte,
  dopo,
  usi,
}: {
  attivita: Attivita[];
  tutte: number;
  dopo: () => Promise<void>;
  usi: Map<string, UsoAttivita>;
}) {
  const filtrate = attivita.length !== tutte;
  // Chiusi per tipo di lavoro: si legge quanti codici ci sono in ognuno e si apre quello che serve. Cercando,
  // si aprono da soli quelli che hanno risultati.
  const [apertura, setApertura] = useState<"auto" | "aperti" | "chiusi">("auto");
  return (
    <section className="elenco">
      <div className="catalogo-testa">
        <h2>
          {filtrate
            ? `${attivita.length} di ${conta(tutte, "attività", "attività")}`
            : conta(tutte, "attività in catalogo", "attività in catalogo")}
        </h2>
        <span className="registro-apri">
          <button type="button" className="tasto tasto-nudo" onClick={() => setApertura("aperti")}>
            Apri tutti
          </button>
          <button type="button" className="tasto tasto-nudo" onClick={() => setApertura("chiusi")}>
            Chiudi tutti
          </button>
        </span>
      </div>
      {TIPI_PRATICA.map((tipo) => {
        const sue = attivita.filter((a) => a.tipi_pratica.includes(tipo.valore));
        // Cercando, un tipo senza risultati non si mostra: sarebbe rumore.
        if (filtrate && sue.length === 0) return null;
        return (
          <details
            key={`${tipo.valore}-${apertura}`}
            className="gruppo gruppo-pieghevole"
            open={apertura === "aperti" || (apertura === "auto" && filtrate)}
          >
            <summary className="gruppo-testa">
              <h3>{tipo.nome}</h3>
              <span className="cifra">{sue.length}</span>
            </summary>
            {sue.length === 0 ? (
              <p className="aiuto">
                Nessuna attività: su una pratica di questo tipo non si può ancora
                imputare.
              </p>
            ) : (
              <table className="tabella catalogo-tabella">
                <tbody>
                  {sue.map((a) => (
                    <tr key={a.codice}>
                      <td className="colonna-codice">{a.codice}</td>
                      <td>
                        {a.descrizione}
                        <span className="riga-secondaria">
                          {a.da_rivedere && (
                            <span className="narrativa">
                              Proposta da un collega, da rivedere:{" "}
                              <Link to="/fatturare/da-fatturare">scegli la frase per la fattura</Link>
                            </span>
                          )}
                          <FraseAttivita attivita={a} dopo={dopo} />
                          {a.tipi_pratica.length > 1 && (
                            <span className="narrativa">
                              vale anche per{" "}
                              {a.tipi_pratica
                                .filter((t) => t !== tipo.valore)
                                .map(nomeTipo)
                                .join(", ")}
                            </span>
                          )}
                          <UsoDellAttivita uso={usi.get(a.codice)} />
                        </span>
                      </td>
                      <td className="colonna-importo">
                        <PrezzoAttivita attivita={a} dopo={dopo} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </details>
        );
      })}
    </section>
  );
}

/** Quanto e' usato un codice: serve a riconoscere cio' che non si usa piu' (un doppione, una voce vecchia). */
function UsoDellAttivita({ uso }: { uso: UsoAttivita | undefined }) {
  if (uso === undefined) return <span className="narrativa uso-codice mai">mai usata</span>;
  return (
    <span className="narrativa uso-codice">
      usata in {conta(uso.registrazioni, "registrazione", "registrazioni")}
      {uso.ultima && `, l'ultima il ${dataCompleta(uso.ultima)}`}
    </span>
  );
}

/** Il prezzo di una riga, e il modo di cambiarlo. Vuoto vuol dire "a ore". */
function PrezzoAttivita({ attivita, dopo }: { attivita: Attivita; dopo: () => Promise<void> }) {
  const [apri, setApri] = useState(false);
  const [prezzo, setPrezzo] = useState(attivita.prezzo !== null ? euro(attivita.prezzo) : "");
  const [unita, setUnita] = useState(attivita.unita);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (!apri) {
    return (
      <button type="button" className="tasto tasto-nudo" onClick={() => setApri(true)}>
        {attivita.prezzo === null
          ? "a ore"
          : `${euro(attivita.prezzo)}${attivita.unita ? ` a ${attivita.unita}` : ""}`}
      </button>
    );
  }

  async function salva(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    let letto: string | null = null;
    if (prezzo.trim()) {
      letto = importoDaScritto(prezzo);
      if (letto === null) return setErrore("Il prezzo non si legge.");
    }
    setInCorso(true);
    try {
      await anagrafica.cambiaPrezzo(attivita.codice, letto, letto === null ? "" : unita.trim());
      setApri(false);
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="prezzo-riga" onSubmit={salva} noValidate>
      <input
        aria-label={`Prezzo di ${attivita.codice}, vuoto per a ore`}
        inputMode="decimal"
        placeholder="a ore"
        value={prezzo}
        onChange={(e) => setPrezzo(e.target.value)}
      />
      <input
        aria-label="Unità, vuota per a prestazione"
        placeholder="per unità, es. fabbricato"
        value={unita}
        onChange={(e) => setUnita(e.target.value)}
      />
      <button className="tasto tasto-piccolo" type="submit" disabled={inCorso}>
        Salva
      </button>
      <button type="button" className="tasto tasto-nudo" onClick={() => setApri(false)}>
        Lascia
      </button>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
    </form>
  );
}

/** La frase dell'attivita' nel testo per Fatture Smart, e il modo di cambiarla. */
function FraseAttivita({ attivita, dopo }: { attivita: Attivita; dopo: () => Promise<void> }) {
  const [apri, setApri] = useState(false);
  const [frase, setFrase] = useState(attivita.testo_fattura);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (!apri) {
    return (
      <span className="frase-fattura">
        <span className="narrativa">
          In fattura: {attivita.testo_fattura || "come il nome dell'attività"}
        </span>
        <button
          type="button"
          className="tasto tasto-nudo"
          onClick={() => {
            setFrase(attivita.testo_fattura);
            setApri(true);
          }}
        >
          cambia
        </button>
      </span>
    );
  }

  async function salva(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await anagrafica.cambiaTestoFattura(attivita.codice, frase);
      setApri(false);
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="frase-fattura-modulo" onSubmit={salva} noValidate>
      <input
        aria-label={`Frase di ${attivita.codice} nel testo per Fatture Smart, vuota per il nome`}
        value={frase}
        maxLength={500}
        placeholder="come il nome dell'attività"
        onChange={(e) => setFrase(e.target.value)}
      />
      <button className="tasto tasto-piccolo" type="submit" disabled={inCorso}>
        Salva
      </button>
      <button type="button" className="tasto tasto-nudo" onClick={() => setApri(false)}>
        Lascia
      </button>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
    </form>
  );
}

function NuovaAttivita({ dopo }: { dopo: () => Promise<void> }) {
  const [codice, setCodice] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [testoFattura, setTestoFattura] = useState("");
  const [tipi, setTipi] = useState<string[]>([]);
  const [prezzo, setPrezzo] = useState("");
  const [unita, setUnita] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  function commuta(valore: string) {
    setTipi((attuali) =>
      attuali.includes(valore)
        ? attuali.filter((t) => t !== valore)
        : [...attuali, valore],
    );
  }

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    if (tipi.length === 0) {
      setErrore(
        "Scegli almeno un tipo di lavoro: un'attività che non vale per niente non comparirebbe mai a chi imputa.",
      );
      return;
    }
    let prezzoLetto: string | null = null;
    if (prezzo.trim()) {
      prezzoLetto = importoDaScritto(prezzo);
      if (prezzoLetto === null) return setErrore("Il prezzo non si legge.");
    }
    setInCorso(true);
    try {
      await anagrafica.creaAttivita({
        codice,
        descrizione,
        tipi_pratica: tipi,
        prezzo: prezzoLetto,
        unita: prezzoLetto === null ? "" : unita.trim(),
        testo_fattura: testoFattura.trim(),
      });
      setCodice("");
      setDescrizione("");
      setTestoFattura("");
      setTipi([]);
      setPrezzo("");
      setUnita("");
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo riquadro" onSubmit={invia} noValidate>
      <h2>Attività nuova</h2>

      <div className="imputa-riga">
        <div className="campo campo-minuti">
          <label htmlFor="codice">Codice</label>
          <input
            id="codice"
            value={codice}
            required
            placeholder="BIL01"
            onChange={(e) => setCodice(e.target.value)}
          />
        </div>
        <div className="campo campo-largo">
          <label htmlFor="descrizione">Descrizione</label>
          <input
            id="descrizione"
            value={descrizione}
            required
            placeholder="Riclassificazione del bilancio di verifica"
            onChange={(e) => setDescrizione(e.target.value)}
          />
        </div>
      </div>

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="testo-fattura">Come compare nella fattura (facoltativo)</label>
          <input
            id="testo-fattura"
            value={testoFattura}
            maxLength={500}
            placeholder="Predisposizione ed invio telematico dichiarazione IVA annuale"
            onChange={(e) => setTestoFattura(e.target.value)}
          />
        </div>
      </div>

      <div className="imputa-riga">
        <div className="campo campo-minuti">
          <label htmlFor="prezzo">Prezzo fisso</label>
          <input
            id="prezzo"
            inputMode="decimal"
            value={prezzo}
            placeholder="vuoto: a ore"
            onChange={(e) => setPrezzo(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="unita">Per unità</label>
          <input
            id="unita"
            value={unita}
            placeholder="es. fabbricato"
            disabled={!prezzo.trim()}
            onChange={(e) => setUnita(e.target.value)}
          />
        </div>
      </div>
      <p className="aiuto">
        Senza prezzo l'attività si fattura a ore, con le tariffe. Con il prezzo vale
        quello, per prestazione; se scrivi un'unità, vale per ognuna, e chi imputa
        scrive quante.
      </p>

      <fieldset className="interruttori interruttori-fitti">
        <legend>Per quali tipi di lavoro vale</legend>
        {TIPI_PRATICA.map((t) => (
          <Interruttore
            key={t.valore}
            titolo={t.nome}
            acceso={tipi.includes(t.valore)}
            cambia={() => commuta(t.valore)}
            corto
          />
        ))}
      </fieldset>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div>
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto salvando" : "Aggiungi al catalogo"}
        </button>
      </div>
    </form>
  );
}
